<template lang="pug">
.questions-pane
    aiq-tabs.pane-tabs(v-model="activeTab" type="card")
        //- LEFT TAB 1: ordered, draggable question list.
        aiq-tab-pane(:label="`${$t(`${baseTranslationPath}.tab_questions`)} (${questions.length})`" name="questions")
            .pane-actions
                aiq-button(size="small" type="primary" :disabled="!fields.length" @click="$emit('add-question')")
                    | {{ $t(`${baseTranslationPath}.add_question`) }}
            .list-scroll
                draggable.q-list(
                    :list="orderedQuestions"
                    item-key="id"
                    handle=".drag-handle"
                    @change="onReorder"
                )
                    template(#item="{ element, index }")
                        .q-item(
                            :class="{ active: element.id === selectedQuestionId }"
                            @click="$emit('select-question', element)"
                        )
                            font-awesome-icon.drag-handle(:icon="['fas', 'grip-vertical']")
                            .q-index {{ index + 1 }}
                            .q-body
                                .q-prompt {{ element.prompt || element.question_key }}
                                .q-meta
                                    span.tag {{ element.answer_type }}
                                    span.tag(v-if="element.interaction_class") {{ element.interaction_class }}
                                    span.tag.manual(v-if="element.source === 'manual'") {{ $t(`${baseTranslationPath}.manual`) }}
                                    span.tag.group-tag(
                                        v-for="g in questionGroups(element)"
                                        :key="`qg-${g.id}`"
                                    )
                                        font-awesome-icon.gt-icon(:icon="['fas', 'layer-group']")
                                        | {{ g.label || g.group_key }}
                            font-awesome-icon.q-settings(
                                :icon="['fas', 'ellipsis-vertical']"
                                :title="$t(`${baseTranslationPath}.question_settings_title`)"
                                @click.stop="$emit('open-settings', element)"
                            )
                .empty(v-if="!questions.length") {{ $t(`${baseTranslationPath}.no_questions`) }}
        //- LEFT TAB 2: full field list (from the fields table).
        aiq-tab-pane(:label="`${$t(`${baseTranslationPath}.tab_fields`)} (${fields.length})`" name="fields")
            .list-scroll
                .f-item(
                    v-for="(field, index) in fields"
                    :key="field.id"
                    :class="{ active: field.id === selectedFieldId }"
                    @click="$emit('select-field', field)"
                )
                    .f-index {{ index + 1 }}
                    font-awesome-icon.f-icon(:icon="getFieldIconType(field).icon")
                    .f-body
                        .f-label {{ field.field_name || field.label || field.field_key }}
                        .f-meta
                            span.tag {{ field.field_type }}
                            span.tag.unlinked(v-if="!field.question_ref") {{ $t(`${baseTranslationPath}.unlinked`) }}
                    font-awesome-icon.q-settings(
                        :icon="['fas', 'ellipsis-vertical']"
                        :title="$t(`${baseTranslationPath}.field_settings_title`)"
                        @click.stop="$emit('open-field-settings', field)"
                    )
                .empty(v-if="!fields.length") {{ $t(`${baseTranslationPath}.no_fields`) }}
        //- LEFT TAB 3: groups (structural clusters of fields).
        aiq-tab-pane(:label="`${$t(`${baseTranslationPath}.tab_groups`)} (${groups.length})`" name="groups")
            .pane-actions
                aiq-button(size="small" type="primary" @click="$emit('add-group')")
                    | {{ $t(`${baseTranslationPath}.add_group`) }}
            .list-scroll
                .g-item(
                    v-for="(group, index) in groups"
                    :key="group.id"
                    :class="{ active: group.id === selectedGroupId }"
                    @click="$emit('select-group', group)"
                )
                    .g-index {{ index + 1 }}
                    font-awesome-icon.f-icon(:icon="['fas', 'layer-group']")
                    .g-body
                        .g-label {{ group.label || group.group_key }}
                        .g-meta
                            span.tag(v-if="group.group_type") {{ group.group_type }}
                            span.tag {{ $t(`${baseTranslationPath}.group_field_count`, { count: (group.field_ids || []).length }) }}
                            span.tag(v-if="(group.question_keys || []).length") {{ $t(`${baseTranslationPath}.group_question_count`, { count: group.question_keys.length }) }}
                            span.tag.manual(v-if="group.source === 'manual'") {{ $t(`${baseTranslationPath}.manual`) }}
                    font-awesome-icon.q-settings(
                        :icon="['fas', 'ellipsis-vertical']"
                        :title="$t(`${baseTranslationPath}.group_settings_title`)"
                        @click.stop="$emit('open-group-settings', group)"
                    )
                .empty(v-if="!groups.length") {{ $t(`${baseTranslationPath}.no_groups`) }}
</template>
<script>
import get from 'lodash/get';
import { fieldIconTypes } from './fieldUtil';

export default {
  name: 'QuestionsPane',
  props: {
    questions: {
      type: Array,
      default: () => [],
    },
    fields: {
      type: Array,
      default: () => [],
    },
    selectedQuestionId: {
      type: [String, Number],
      default: null,
    },
    selectedFieldId: {
      type: [String, Number],
      default: null,
    },
    groups: {
      type: Array,
      default: () => [],
    },
    selectedGroupId: {
      type: [String, Number],
      default: null,
    },
  },
  emits: ['select-question', 'select-field', 'reorder-question', 'add-question', 'open-settings', 'open-field-settings', 'select-group', 'add-group', 'open-group-settings'],
  data() {
    return {
      baseTranslationPath: 'iq_tools_tab.forms_tab.manage',
      activeTab: 'questions',
      orderedQuestions: [],
    };
  },
  watch: {
    questions: {
      immediate: true,
      handler(next) {
        // local mirror the draggable list mutates; keep parent order as the source.
        this.orderedQuestions = [...next];
      },
    },
  },
  methods: {
    getFieldIconType(field) {
      return fieldIconTypes(get(field, 'field_type'));
    },
    // Groups whose members include this question (link is derived, not stored).
    questionGroups(question) {
      const key = get(question, 'question_key');
      if (!key) {
        return [];
      }
      return (this.groups || []).filter(g => (g.question_keys || []).includes(key));
    },
    onReorder(evt) {
      const moved = get(evt, 'moved');
      if (!moved) {
        return;
      }
      // vuedraggable already reordered orderedQuestions; report the new index.
      this.$emit('reorder-question', {
        question: moved.element,
        newIndex: moved.newIndex,
      });
    },
  },
};
</script>
<style lang="scss" scoped>
@import 'src/styles/aiq-variables.scss';

.questions-pane {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background-color: var(--aiq-bgc-middle-column-content);
}

.pane-tabs {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;

  :deep(.el-tabs__content) {
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  :deep(.el-tab-pane) {
    height: 100%;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }
}

.pane-actions {
  padding: 8px 10px;
  border-bottom: 1px solid $aiq-tab-content-border;
}

.list-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 10px;
}

.q-item,
.f-item,
.g-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  border: 1px solid $aiq-icon-primary;
  border-radius: 5px;
  margin-bottom: 8px;
  cursor: pointer;
  background-color: $aiq-bgc-list-item;

  &:hover {
    background-color: $aiq-tab-content-background;
    border-color: $aiq-tab-content-background;
  }

  &.active {
    background-color: $aiq-tab-content-background;
    border-color: $aiq-bgc-button-primary;
  }
}

.drag-handle {
  cursor: grab;
  color: $aiq-input-border-hover;
}

.q-index,
.f-index,
.g-index {
  width: 22px;
  height: 22px;
  border-radius: 4px;
  background-color: $aiq-color-black;
  color: $aiq-color-white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
}

.q-body,
.f-body,
.g-body {
  min-width: 0;
  flex: 1;
}

.q-settings {
  color: $aiq-input-border-hover;
  font-size: 15px;
  padding: 4px 6px;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    color: $aiq-bgc-button-primary;
  }
}

.q-prompt,
.f-label,
.g-label {
  font-size: 14px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.q-meta,
.f-meta,
.g-meta {
  display: flex;
  gap: 6px;
  margin-top: 4px;
  flex-wrap: wrap;
}

.f-icon {
  color: $aiq-input-border-hover;
  font-size: 16px;
}

.tag {
  font-size: 11px;
  color: $aiq-input-border-hover;
  border: 1px solid $aiq-icon-primary;
  border-radius: 3px;
  padding: 0 5px;

  &.manual {
    color: $aiq-bgc-button-primary;
    border-color: $aiq-bgc-button-primary;
  }

  &.unlinked {
    color: $aiq-color-error;
    border-color: $aiq-color-error;
  }

  &.group-tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: $aiq-color-warning;
    border-color: $aiq-color-warning;

    .gt-icon {
      font-size: 10px;
    }
  }
}

.empty {
  color: $aiq-color-text-secondary;
  font-size: 13px;
  padding: 16px;
  text-align: center;
}
</style>
